/**
 * Copyright (c) 2021-2026 Kognitos, Inc. All rights reserved.
 */
import { type TAuthProviderConfig } from './authProviders';

/**
 * Runtime configuration injected into window.__RUNTIME_CONFIG__.
 *
 * - Development: Vite plugin fetches from AWS SSM and injects via transformIndexHtml
 * - Production: nginx sub_filter injects at container startup from environment variables
 *
 * This enables "build once, deploy everywhere" by separating config from the build.
 */
export interface RuntimeConfig {
  /** Omitted together by a federated-only deployment with no Auth0 tenant. */
  auth0Domain?: string;
  auth0ClientId?: string;
  auth0Audience?: string;
  sentryDsn?: string;
  statsigClientId?: string;
  intercomAppId?: string;
  environment?: string;
  clusterPublicIp?: string;
  /** Base URL of the (temporary) ai-in-finance backend. Optional: unset in
   * environments that don't enable the finance workspace. */
  financeBackendUrl?: string;
  /** Configured identity providers, Auth0 first when present. Optional: absent
   * in fixtures and in any host that predates the field. */
  authProviders?: TAuthProviderConfig[];
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Get runtime configuration.
 *
 * Config is always present - injected by Vite (dev) or nginx (production).
 * E2E tests verify config injection works correctly.
 *
 * @throws Error if config is not found (helps debug nginx/docker issues)
 */
export function getRuntimeConfig(): RuntimeConfig {
  const config = window.__RUNTIME_CONFIG__;

  if (!config) {
    const error = new Error(
      'Runtime configuration not found. ' +
        'Config must be injected before app loads. ' +
        'Check nginx sub_filter configuration and docker-entrypoint.sh',
    );
    console.warn(error);
    throw error;
  }

  return config;
}
